import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { LoaderCircle } from 'lucide-react'
import { submittalStatusVariant } from '@/lib/status-variants'
import Grid from '../../../components/grid'

const DASH = '—'

interface SubmittalListItem {
  id: string
  displayNumber: string
  title: string
  companyName: string | null
  status: string
  dueDate: string | null
  responsible: { id: string; name: string } | null
  specSection: { id: string; value: string } | null
  category: { id: string; value: string } | null
  discipline: { id: string; value: string } | null
  package: { id: string; value: string } | null
  currentRevision: { id: string; index: number } | null
  reviewProgress: { decided: number; total: number }
  currentOwners: { id: string; name: string }[]
}

interface SubmittalsListProps {
  items: SubmittalListItem[]
  loading?: boolean
  onRowClick: (row: { id: string }) => void
}

/**
 * Purely presentational: turns a (possibly already filtered/grouped) slice of
 * `Submittal[]` into a grid. Reused for the flat "Items" tab and once per
 * section for the "Packages"/"Spec sections" tabs — grouping and tab state
 * live in the parent (`pages/submittals/index.tsx`).
 */
const SubmittalsList: React.FC<SubmittalsListProps> = ({ items, loading, onRowClick }) => {
  const { t } = useTranslation()

  const rows = items.map((s) => ({
    id: s.id,
    status: s.status,
    displayNumber: s.displayNumber,
    rev: s.currentRevision ? `Rev ${s.currentRevision.index}` : DASH,
    title: s.title,
    specSection: s.specSection?.value ?? DASH,
    category: s.category?.value ?? DASH,
    discipline: s.discipline?.value ?? DASH,
    company: s.companyName || DASH,
    package: s.package?.value ?? DASH,
    responsible: s.responsible?.name ?? DASH,
    dueDate: s.dueDate,
    review: s.reviewProgress,
    currentOwners: s.currentOwners,
    ballInCourt: s.currentOwners,
  }))

  const columns = [
    { title: t('Submittals_table_status'), name: 'status', width: 140 },
    { title: t('Submittals_table_number'), name: 'displayNumber', width: 110, sticky: true },
    { title: t('Submittals_table_rev'), name: 'rev', width: 70 },
    { title: t('Submittals_table_title'), name: 'title', width: 220 },
    { title: t('Submittals_table_spec_section'), name: 'specSection', width: 130 },
    { title: t('Submittals_table_category'), name: 'category', width: 130 },
    { title: t('Submittals_table_discipline'), name: 'discipline', width: 130 },
    { title: t('Submittals_table_company'), name: 'company', width: 150 },
    { title: t('Submittals_table_package'), name: 'package', width: 130 },
    { title: t('Submittals_table_responsible'), name: 'responsible', width: 140 },
    { title: t('Submittals_table_ball_in_court'), name: 'ballInCourt', width: 150 },
    { title: t('Submittals_table_due_date'), name: 'dueDate', width: 110 },
    { title: t('Submittals_table_review'), name: 'review', width: 90 },
  ]

  const formatters = [
    {
      formatter: ({ value }: { value: string }) => (
        <Badge variant={submittalStatusVariant(value)} className="font-medium whitespace-nowrap">
          {t(`Submittal_status_${value}`)}
        </Badge>
      ),
      for: ['status'],
    },
    {
      formatter: ({ value }: { value: string | null }) => {
        if (!value) return DASH
        return new Date(value).toLocaleDateString(t('date_locale') || 'en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      },
      for: ['dueDate'],
    },
    {
      formatter: ({ value, row }: { value: { decided: number; total: number }; row: any }) => {
        const names = (row.currentOwners || []).map((u: any) => u.name).join(', ')
        return (
          <span title={names || undefined} className="text-sm">
            {value.decided}/{value.total}
          </span>
        )
      },
      for: ['review'],
    },
    {
      formatter: ({ value }: { value: string }) => {
        if (!value || value.length <= 40) return value || DASH
        return (
          <span title={value} className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
            {value}
          </span>
        )
      },
      for: ['title'],
    },
    {
      formatter: ({ value }: { value: { id: string; name: string }[] }) => {
        const names = (value || []).map((u) => u.name).join(', ')
        if (!names) return DASH
        return (
          <span title={names} className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm">
            {names}
          </span>
        )
      },
      for: ['ballInCourt'],
    },
  ]

  return (
    <div className="relative min-h-[160px]">
      {loading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
          <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      <Grid
        columns={columns}
        rows={rows}
        formatters={formatters}
        showHeader={true}
        sorting={true}
        sortingColumnExtensions={[
          { columnName: 'review', sortingEnabled: false },
          { columnName: 'ballInCourt', sortingEnabled: false },
        ]}
        defaultSorting={[{ columnName: 'displayNumber', direction: 'desc' }]}
        noDataMessage={t('Submittals_table_no_data')}
        onRowClick={onRowClick}
      />
    </div>
  )
}

export default SubmittalsList
